import { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTrash, faFileCode } from "@fortawesome/free-solid-svg-icons";
import {
  useJpkFiles,
  useGenerateJpk,
  useDeleteJpk,
  downloadJpkFile,
} from "api/accountingService";
import { ENotificationType, notification } from "utils/notification";

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

const CURRENT_YEAR = new Date().getFullYear();
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const fmt = (v: number): string =>
  v || v === 0 ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
const plDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString("pl-PL") : "");
const plDateTime = (iso: string) => (iso ? new Date(iso).toLocaleString("pl-PL") : "");

const JPK_TYPES = [
  { value: "JPK_V7M", label: "JPK_V7M (VAT miesięczny)" },
  { value: "JPK_V7K", label: "JPK_V7K (VAT kwartalny)" },
];

const JpkRegister = ({ searchText, setSubtitle }: Props) => {
  const [type, setType] = useState("JPK_V7M");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());

  const { data: files = [], isLoading } = useJpkFiles();
  const { mutate: generate, isLoading: generating } = useGenerateJpk();
  const { mutate: remove } = useDeleteJpk();

  useEffect(() => {
    setSubtitle(`Rejestr JPK · ${CURRENT_YEAR}`);
  }, [setSubtitle]);

  const filtered = files.filter((f) => {
    const q = searchText.trim().toLowerCase();
    if (!q) return true;
    return f.fileName.toLowerCase().includes(q) || f.type.toLowerCase().includes(q);
  });

  const handleGenerate = () => {
    generate(
      { type, from, to },
      {
        onSuccess: (f) =>
          notification({
            title: "JPK",
            text: `Wygenerowano ${f.type} (${f.rowCount} wierszy VAT).`,
            type: ENotificationType.INFO,
          }),
        onError: () =>
          notification({
            title: "JPK",
            text: "Nie udało się wygenerować pliku JPK.",
            type: ENotificationType.ERROR,
          }),
      },
    );
  };

  const handleDownload = async (id: string, fileName: string) => {
    try {
      await downloadJpkFile(id, fileName);
    } catch {
      notification({ title: "JPK", text: "Nie udało się pobrać pliku.", type: ENotificationType.ERROR });
    }
  };

  const cell: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" };
  const num: React.CSSProperties = { ...cell, textAlign: "right" };
  const head: React.CSSProperties = {
    padding: "8px 10px",
    borderBottom: "2px solid #e5e0ec",
    color: "#79767a",
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
    position: "sticky",
    top: 0,
    background: "#fdf8fd",
  };
  const iconBtn: React.CSSProperties = { background: "none", border: "none", cursor: "pointer", padding: 4, color: "#5d49c7" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fdf8fd" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 16px", flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Typ JPK</InputLabel>
          <Select label="Typ JPK" value={type} onChange={(e) => setType(e.target.value)}>
            {JPK_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Okres od" type="date" size="small" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="Okres do" type="date" size="small" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          style={{
            height: 40,
            padding: "0 18px",
            border: "none",
            borderRadius: 6,
            background: generating ? "#1c1b1f1f" : "#5d49c7",
            color: generating ? "#1c1b1f61" : "#fff",
            fontSize: 13,
            cursor: generating ? "default" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FontAwesomeIcon icon={faFileCode} />
          {generating ? "Generuję…" : "Generuj JPK"}
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={head}>Typ</th>
              <th style={head}>Okres</th>
              <th style={head}>Plik</th>
              <th style={{ ...head, textAlign: "right" }}>Wiersze</th>
              <th style={{ ...head, textAlign: "right" }}>Netto</th>
              <th style={{ ...head, textAlign: "right" }}>VAT</th>
              <th style={head}>Wygenerowano</th>
              <th style={head}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td style={cell} colSpan={8}>Ładowanie…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td style={{ ...cell, color: "#aaa", fontStyle: "italic" }} colSpan={8}>Brak wygenerowanych plików JPK.</td></tr>
            ) : (
              filtered.map((f) => (
                <tr key={f.id}>
                  <td style={cell}>{f.type}</td>
                  <td style={cell}>{plDate(f.periodFrom)} – {plDate(f.periodTo)}</td>
                  <td style={cell}>{f.fileName}</td>
                  <td style={num}>{f.rowCount}</td>
                  <td style={num}>{fmt(f.netAmount)}</td>
                  <td style={num}>{fmt(f.vatAmount)}</td>
                  <td style={cell}>{plDateTime(f.createdAt)}</td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    <button type="button" style={iconBtn} title="Pobierz XML" onClick={() => handleDownload(f.id, f.fileName)}>
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <button type="button" style={{ ...iconBtn, color: "#c44" }} title="Usuń" onClick={() => remove(f.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JpkRegister;
